import { FC, useCallback, useEffect } from 'react';
// routes
import Router from './routes';
// hooks
import useAuth from 'src/hooks/useAuth';
// components
import ThemeConfig from './theme';
import RtlLayout from './components/RtlLayout';
import ScrollToTop from './components/ScrollToTop';
import LoadingScreen from './components/LoadingScreen';
import GoogleAnalytics from './components/GoogleAnalytics';
import NotistackProvider from './components/NotistackProvider';
import { useLocation, useNavigate } from 'react-router';
import { COMPLIANCE_PATHS } from './routes/paths';
import axiosInstance from 'src/utils/axios';
import './App.css';
import './styles/Main.scss';
import { shallowEqual, useSelector } from 'react-redux';

import 'ag-grid-enterprise';
import { LicenseManager } from 'ag-grid-enterprise';
import ErrorBoundary from './components/ErrorBoundary';
import { RealTimeProvider } from './contexts/RealTimeProvider';
// analytics
import { setUserIdentification } from './utils/analytics';
import { RootState } from 'src/redux/rootReducerType';
import { Userpilot } from 'userpilot';

const SETUP_PATHNAME = ['/', '/workbench', '/workbench/'];
// build commit
// ----------------------------------------------------------------------
const App: FC = () => {
  LicenseManager.setLicenseKey(
    'Using_this_{AG_Grid}_Enterprise_key_{AG-085159}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Pronto_Housing}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{ProntoHousing}_only_for_{2}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{ProntoHousing}_need_to_be_licensed___{ProntoHousing}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{5_November_2026}____[v3]_[01]_MTc5MzgzNjgwMDAwMA==d767100ca9b27f353a610b742e1a05ce'
  );
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isInitialized, isAuthenticated, user } = useAuth();

  // UserPilot — initialize when feature flag is on
  const USERPILOT_KEY = import.meta.env.VITE_USERPILOT_KEY || 'STG-NX-44208137';
  const userpilotEnabled = useSelector(
    (state: RootState) => state.featureFlags.flags?.USERPILOT_ENABLED,
    shallowEqual
  );

  useEffect(() => {
    if (userpilotEnabled && USERPILOT_KEY) {
      Userpilot.initialize(USERPILOT_KEY);
    }
  }, [userpilotEnabled]);

  // UserPilot — reload on every route change (SPA requirement)
  useEffect(() => {
    if (isAuthenticated && userpilotEnabled && USERPILOT_KEY) {
      Userpilot.reload();
    }
  }, [pathname, isAuthenticated, userpilotEnabled]);

  // Set user identification when user is available
  useEffect(() => {
    if (isAuthenticated && user) {
      setUserIdentification(user);
    }
  }, [isAuthenticated, user]);

  const checkSetupProperties = useCallback((): void => {
    axiosInstance
      .get(`property_portfolio/property/?fields=setup_completed`)
      .then((response) => {
        if (response.status === 200) {
          const hasPropertiesToSetup = response.data.some(
            (property) => property.setup_completed === false
          );
          if (hasPropertiesToSetup) {
            navigate(COMPLIANCE_PATHS.property.setupList);
          }
        }
      })
      .catch((_error) => {
        // Handle error silently
      });
  }, [navigate]);

  useEffect(() => {
    if (
      isAuthenticated &&
      isInitialized &&
      user.isCompliance &&
      (SETUP_PATHNAME.includes(pathname) || pathname.includes('auth/'))
    ) {
      checkSetupProperties();
    }
  }, [isAuthenticated, isInitialized, user]); // eslint-disable-line

  return (
    <ThemeConfig>
      <RtlLayout>
        <NotistackProvider>
          <RealTimeProvider>
            <ErrorBoundary>
              <ScrollToTop />
              <GoogleAnalytics />
              {isInitialized ? <Router /> : <LoadingScreen />}
            </ErrorBoundary>
          </RealTimeProvider>
        </NotistackProvider>
      </RtlLayout>
    </ThemeConfig>
  );
};

export default App;
